/**
 * **输入画像（InputTraits）—— 纯数据 + 纯函数。**
 *
 * 它回答的不是“当前是什么设备”，而是“最近这几百毫秒，交互表现出哪些性质”。
 * 每个性质独立、可以同时为真；UI 按**具体问题**挑用哪一条（要不要画 hover、
 * 目标最小多大、拖拽抖动容忍多少、焦点框显不显示）。
 *
 * ⚠️ **没有“模式”**。不写 `if (isTouch)` —— 每个分支都必须能回答“它是从哪个
 *    事件属性来的”。`pointerType` 只是**最近一次**，能力位（canHover/finePointer）
 *    一旦观察到就保留；易失位（keyboardActive/gamepadActive）超时会被 `decayTraits` 清掉。
 *
 * 这一层不 import Pixi、不碰 window，所以能在 node 里用合成事件断言。
 * DOM 接线在 `profile.ts`。
 */

/** `PointerEvent.pointerType` 归一化后的三档 + 认不出 */
export type PointerKind = 'mouse' | 'pen' | 'touch' | 'unknown';

export interface InputTraits {
  /** 最近一次指针事件报的类型 */
  readonly kind: PointerKind;
  /** 接触面几何：`max(width,height)`，CSS px。鼠标约 1，指腹 20–50 */
  readonly contactPx: number;
  /** 压力，0..1 */
  readonly pressure: number;
  /** 能不能 hover（能力位）：观察到无按键移动，或最近一次是 mouse/pen */
  readonly canHover: boolean;
  /** 精确指针（能力位）：mouse/pen 且接触面很小 */
  readonly finePointer: boolean;
  /** 最近一次输入是不是键盘（易失位） */
  readonly keyboardActive: boolean;
  /** 最近一次输入是不是手柄（易失位） */
  readonly gamepadActive: boolean;
  /** **这一趟会话里见过手柄连接吗**（闩锁位）。设置页据此决定要不要显示手柄一栏 */
  readonly gamepadSeen: boolean;
  /** 最近一次输入的时间（单调毫秒，和 event.timeStamp 同源） */
  readonly lastInputAt: number;
}

/** 指针事件里我们用得到的字段（从 PointerEvent 摘出来，方便合成） */
export interface PointerSample {
  readonly pointerType: string;
  readonly width: number;
  readonly height: number;
  readonly pressure: number;
  /** PointerEvent.buttons：0 = 没有按键 */
  readonly buttons: number;
}

export type InputSample =
  | { readonly kind: 'pointer'; readonly at: number; readonly pointer: PointerSample }
  | { readonly kind: 'key'; readonly at: number; readonly modifierOnly: boolean }
  | { readonly kind: 'gamepad'; readonly at: number }
  /** 只是“连上了”——闩住 `gamepadSeen`，不算一次“在用” */
  | { readonly kind: 'gamepad-connected'; readonly at: number };

/** 易失位闲置这么久就归零（键盘/手柄）。能力位不受影响 */
export const TRAITS_TTL_MS = 1500;
/** 接触面小于它才算“精确指针”。鼠标是 1 */
export const FINE_CONTACT_MAX = 4;
/** 媒体查询说这是粗指针时，先给这个接触面当初值 */
export const COARSE_CONTACT_PX = 24;

export function normalizePointerKind(pointerType: string): PointerKind {
  return pointerType === 'mouse' || pointerType === 'pen' || pointerType === 'touch'
    ? pointerType
    : 'unknown';
}

export function initialTraits(): InputTraits {
  return {
    kind: 'unknown',
    contactPx: 0,
    pressure: 0,
    canHover: false,
    finePointer: false,
    keyboardActive: false,
    gamepadActive: false,
    gamepadSeen: false,
    lastInputAt: 0,
  };
}

/**
 * 媒体查询只当**初值**：命中 `(hover: none) and (pointer: coarse)` 时先按粗指针起步，
 * 事件一到立刻覆盖。它不是判据，只是“首帧就大一点”的默认。
 */
export function coarseSeed(): InputTraits {
  return { ...initialTraits(), contactPx: COARSE_CONTACT_PX };
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** 吃一个输入样本，得到新的画像。纯函数 */
export function reduceTraits(current: InputTraits, sample: InputSample): InputTraits {
  if (sample.kind === 'pointer') {
    const kind = normalizePointerKind(sample.pointer.pointerType);
    const contactPx = Math.max(0, sample.pointer.width, sample.pointer.height);
    const pressure = clamp01(sample.pointer.pressure);
    if (kind === 'touch') {
      return {
        ...current,
        kind,
        contactPx,
        pressure,
        canHover: false,
        finePointer: false,
        keyboardActive: false,
        gamepadActive: false,
        lastInputAt: sample.at,
      };
    }
    const hoveringWithoutButton = sample.pointer.buttons === 0;
    return {
      ...current,
      kind,
      contactPx,
      pressure,
      // mouse/pen 天生能 hover；认不出的类型就看“有没有无按键移动”
      canHover: kind === 'mouse' || kind === 'pen' || hoveringWithoutButton || current.canHover,
      finePointer: (kind === 'mouse' || kind === 'pen') && contactPx <= FINE_CONTACT_MAX,
      keyboardActive: false,
      gamepadActive: false,
      lastInputAt: sample.at,
    };
  }
  if (sample.kind === 'key') {
    // 纯修饰键不算“在用键盘”（否则按住 Shift 也会把焦点框闪出来）
    if (sample.modifierOnly) return current;
    return {
      ...current,
      keyboardActive: true,
      gamepadActive: false,
      lastInputAt: sample.at,
    };
  }
  if (sample.kind === 'gamepad-connected') {
    return { ...current, gamepadSeen: true, lastInputAt: sample.at };
  }
  return {
    ...current,
    gamepadActive: true,
    gamepadSeen: true,
    keyboardActive: false,
    lastInputAt: sample.at,
  };
}

/** 超过 TTL 就把易失位清掉。能力位保留 */
export function decayTraits(current: InputTraits, now: number): InputTraits {
  if (now - current.lastInputAt <= TRAITS_TTL_MS) return current;
  if (!current.keyboardActive && !current.gamepadActive) return current;
  return { ...current, keyboardActive: false, gamepadActive: false };
}

/** 目标该按“粗指针”加大吗（P1 的控件用）。先给个纯函数，测试与后续共用 */
export function prefersLargeTargets(traits: InputTraits): boolean {
  return !traits.finePointer || traits.contactPx >= COARSE_CONTACT_PX;
}
